<template>
  <block>
    <div
      v-if="hasFilterFields || hasComputedTopActions || computedConfigEntries"
      class="action-bar flex-between flex-wrap padding-m-vertical margin-xl-bottom relative z-level-2 gap-l">
      <div class="filter-controls flex gap-m">
        <form-button
          v-if="computedConfigEntries"
          v-tooltip="`core.crudV1.actions.configTooltip`"
          @click="isShowingConfigEntries = true"
          icon="settings"
          square
          slim
          :text="false">
        </form-button>
        <form-button
          v-if="hasFilterFields"
          icon="magnify"
          :theme="isShowingFilterFields ? 'alt' : 'gray-5'"
          text="core.crudV1.filterToggleText"
          @click.prevent="
            isShowingFilterFields = !isShowingFilterFields
          "></form-button>
        <form-button
          v-if="hasActiveRemoveableActiveFilters"
          icon="x"
          theme="danger"
          :loading="asyncOps.asyncStatus.asyncDataLoading || willFilterSoon"
          text="core.crudV1.clearFilters"
          @click.prevent="resetFilters"></form-button>
        <slot
          name="general-buttons"
          v-bind="{
            isAnyItemSelected,
            selectedItems,
            areAllItemsSelected,
            items: displayItems,
          }">
        </slot>
      </div>

      <div
        class="top-actions flex flex-middle gap-l animate__fadeIn animate--fast">
        <slot
          name="general-actions"
          v-bind="{
            listData,
            items,
            isAnyItemSelected,
            selectedItems,
            areAllItemsSelected,
          }">
          <div
            v-for="(action, index) of computedMassActions.custom"
            v-if="
              (isAnyItemSelected && computedMassActions) ||
              hasConstantMassActions
            "
            :key="`custom-${index}`"
            class="">
            <form-button
              v-tooltip="action.tooltip ? action.tooltip : false"
              :icon="action.icon ? action.icon : 'shape-square'"
              :theme="action.theme ? action.theme : 'lead'"
              :show-slot="!!action.text"
              :text="action.text ? action.text : false"
              @click="runCustomMassActionHandler(index, action)"></form-button>
          </div>
          <div
            v-if="
              isAnyItemSelected &&
              computedMassActionsEnabled &&
              computedMassActions.delete
            "
            :key="1"
            class="">
            <form-button
              theme="danger"
              icon="trash"
              @click="initDeleteSelectedItemsFlow"
              >{{ translate("core.crudV1.actions.deleteSelected") }}
            </form-button>
          </div>
          <div
            v-if="computedMassActionsEnabled && computedMassActions.export"
            :key="'mass-action-export'"
            class="">
            <form-button
              theme="inverse"
              icon="arrow-download-alt"
              @click="initExportFlow"
              >{{ translate("core.crudV1.actions.csvExportCta") }}
            </form-button>
          </div>
          <div
            v-if="computedMassActionsEnabled && computedMassActions.import"
            :key="'mass-action-export'"
            class="">
            <form-button
              theme="inverse"
              icon="arrow-upload-alt"
              @click="initImportFlow"
              >{{ translate("core.crudV1.actions.csvImportCta") }}
            </form-button>
          </div>
          <div v-if="computedActions?.baseActions?.create" key="2" class="">
            <form-button
              theme="success"
              class="animate__fadeIn animate--fast"
              icon="plus"
              @click="goToCreate"
              >{{ translate("core.createNew") }}
            </form-button>
          </div>
          <div v-show="hasHiddenFields" class="">
            <form-button
              theme="gray-5"
              class="animate__fadeIn animate--fast"
              icon="eye"
              @click="isShowingFieldToggleUi = !isShowingFieldToggleUi"
              >{{ translate("core.crud.showHideListFields") }}
            </form-button>
          </div>
          <slot
            name="additional-actions"
            v-bind="{
              listData,
              items,
              isAnyItemSelected,
              selectedItems,
              areAllItemsSelected,
            }">
          </slot>
        </slot>
      </div>

      <div
        v-show="isShowingFilterFields && hasFilterFields"
        class="filter width-expand flex flex-gap-l flex-wrap flex-start margin-l-top animate__fadeIn animate--fast margin-m-bottom"
        style="max-width: 100%">
        <form-input
          :as-user="asUser"
          v-for="(field, fieldIndex) of displayFilterFields"
          :key="fieldIndex"
          v-bind="field"
          v-model="desiredFilters[field.name]"
          style="min-width: 200px"
          theme-style="minimal"></form-input>
      </div>
      <div
        class="width-expand flex flex-gap-l flex-wrap flex-start margin-l-top animate__fadeIn animate--fast margin-m-bottom"
        v-show="isShowingFieldToggleUi">
        <form-input
          :as-user="asUser"
          label="core.crud.showHideListFields"
          type="select2"
          class="width-expand"
          :multiple="true"
          :sortable="true"
          v-model="localColumnVisibility"
          :list="toggleableFieldList">
        </form-input>
      </div>
    </div>

    <slot name="before-table" v-bind="{ items }"></slot>
    <h3
      v-if="displayItems.length < 1 && !isLoading"
      class="animate__fadeIn animate--slow">
      {{ translateSafe(noItemsFoundText) }}
    </h3>

    <div v-else class="item-table-wrapper relative z-level-1">
      <bar-loader
        v-if="
          willFilterSoon &&
          (displayItems.length > 0 || !asyncOps.asyncStatus.asyncDataLoading)
        "
        theme="alt"
        style="width: 100%"
        :active="
          willFilterSoon &&
          (displayItems.length > 0 || !asyncOps.asyncStatus.asyncDataLoading)
        "
        :fixed="false"></bar-loader>
      <div class="width-expand overflow-x-scroll">
        <table
          v-show="displayItems.length > 0"
          class="crud-list-table table table--highlight width-expand overflow-x-scroll"
          :class="{ 'is-refreshed': tableRefreshIndicator }">
          <thead>
            <tr>
              <th
                scope="col"
                v-if="computedMassActionsEnabled"
                class="table--shrink table--center item-select-th">
                <form-input
                  :as-user="asUser"
                  type="checkbox"
                  :label="false"
                  :checked="areAllItemsSelected"
                  @click="toggleAllSelection"></form-input>
              </th>
              <th
                scope="col"
                v-if="showIdField"
                class="table--shrink table--center orderable"
                :class="{ 'is-current-ordering': isColCurrentOrdering('id') }"
                @click="quickSetListOrdering('id')">
                <div class="flex flex-start flex-middle gap-s">
                  <span>{{ translate("core.crudV1.fields.id") }}</span>
                  <icon
                    v-if="isColCurrentOrderingAsc('id')"
                    size="1"
                    icon="chevron-up"></icon>
                  <icon
                    v-if="isColCurrentOrderingDesc('id')"
                    size="1"
                    icon="chevron-down"></icon>
                </div>
              </th>

              <th
                scope="col"
                v-if="sortable"
                class="table--shrink table--center orderable"
                :class="{
                  'is-current-ordering': isColCurrentOrdering('order'),
                }"
                @click="quickSetListOrdering('order')">
                <div class="flex flex-start flex-middle gap-s">
                  <span>{{ translate("core.crudV1.fields.order") }}</span>
                  <icon
                    v-if="isColCurrentOrderingAsc('order')"
                    size="1"
                    icon="chevron-up"></icon>
                  <icon
                    v-if="isColCurrentOrderingDesc('order')"
                    size="1"
                    icon="chevron-down"></icon>
                </div>
              </th>

              <th
                scope="col"
                v-for="(field, index) of displayFields"
                key="index"
                class=" "
                :class="{
                  'is-current-ordering': isColCurrentOrdering(field.itemKey),
                  orderable: field.orderable,
                }"
                :field="field"
                @click="
                  field.orderable ? quickSetListOrdering(field.itemKey) : ''
                ">
                <div class="flex flex-start flex-middle gap-s">
                  <span> {{ translate(field.label) }}</span>
                  <icon
                    v-if="isColCurrentOrderingAsc(field.itemKey)"
                    size="1"
                    icon="chevron-up"></icon>
                  <icon
                    v-if="isColCurrentOrderingDesc(field.itemKey)"
                    size="1"
                    icon="chevron-down"></icon>
                </div>
              </th>

              <th
                scope="col"
                v-if="showVersionCountColumn"
                class="table--shrink table--center orderable"
                style="white-space: nowrap">
                <div class="flex flex-start flex-middle gap-s">
                  <span>{{
                    translate("core.crudV1.fields.versionCount")
                  }}</span>
                </div>
              </th>

              <th
                scope="col"
                v-if="showHasUnacceptedVersionSuggestionColumn"
                class="table--shrink table--center orderable"
                style="white-space: nowrap">
                <div class="flex flex-start flex-middle gap-s">
                  <span>{{
                    translate(
                      "core.crudV1.fields.hasUnacceptedVersionSuggestion"
                    )
                  }}</span>
                </div>
              </th>

              <th v-if="computedActions" scope="col">
                {{ translate("core.crudV1.fields.actions") }}
              </th>
            </tr>
          </thead>

          <tbody
            v-sortable="{ onUpdate: onReorder, handle: '[data-role=handle]' }"
            v-show="
              displayItems.length > 0 || !asyncOps.asyncStatus.asyncDataLoading
            ">
            <tr
              v-for="(item, index) of displayItems"
              :key="`${uniqueRowKeyPrefix}-${index}`">
              <td
                :key="item.id ? item.id : getRandomInt()"
                v-if="computedMassActionsEnabled"
                class="table--shrink table--center">
                <form-input
                  :as-user="asUser"
                  type="checkbox"
                  :label="false"
                  :checked="isItemSelected(item)"
                  @click="toggleItemSelection(item)"></form-input>
              </td>
              <td v-if="showIdField" class="table--shrink table--center">
                <span
                  class="inline-block"
                  style="min-width: 50px; text-align: start"
                  >{{ item.id || "-" }}</span
                >
              </td>
              <td v-if="sortable" class="table--shrink table--center">
                <span>
                  <icon
                    v-if="
                      isColCurrentOrderingAsc('order') &&
                      !asyncOps.asyncStatus.asyncDataLoading
                    "
                    icon="align-justify-alt"
                    data-role="handle"
                    style="cursor: move"></icon>
                  <icon
                    v-else
                    icon="align-justify-alt"
                    style="cursor: not-allowed"></icon>
                </span>
              </td>

              <td
                v-for="(field, fieldIndex) of displayFields"
                :key="index + '-' + fieldIndex"
                class=""
                :class="{ 'has-quick-edit': !!field.quickEdit }"
                @click="field.quickEdit ? startQuickEdit(item, field) : ''">
                <slot
                  :name="'cell-' + fieldIndex"
                  v-bind="{ field, item, index, fieldIndex }">
                  <inline-block style="min-width: 120px">
                    <a
                      href="#"
                      @click.prevent="goToEdit(item)"
                      v-if="field.isPrimaryField"
                      >{{ item[field.itemKey] || "-" }}</a
                    >
                    <span v-else>{{ item[field.itemKey] || "-" }}</span>
                  </inline-block>
                </slot>
              </td>

              <td
                v-if="showVersionCountColumn"
                class="table--shrink table--center">
                <span
                  class="inline-block"
                  style="min-width: 50px; text-align: start"
                  >{{ item.versionCount }}</span
                >
              </td>
              <td
                v-if="showHasUnacceptedVersionSuggestionColumn"
                class="table--shrink table--center">
                <span
                  class="inline-block"
                  style="min-width: 50px; text-align: start">
                  <icon
                    icon="check"
                    theme="success"
                    v-if="item.hasUnacceptedVersionSuggestion"></icon>
                  <icon icon="x" theme="error" v-else></icon>
                </span>
              </td>
              <td
                v-if="computedActions"
                class="table--shrink row-actions"
                style="min-width: 150px">
                <slot
                  v-if="computedActions"
                  :name="'cell-actions'"
                  v-bind="{ item, index, crudList: this }">
                  <div class="">
                    <div
                      class="popup-control"
                      v-if="computedIsActionListAsPopup">
                      <form-button
                        theme="lead"
                        @click="showItemActionsPopup({ item, index })"
                        icon="settings"
                        text="core.crudV1.actions.rowActionPopupCta">
                      </form-button>
                    </div>

                    <div class="flex gap-m flex-wrap" v-else>
                      <slot
                        :name="'cell-actions-extra-before'"
                        v-bind="{ item, index, crudList: this }"></slot>

                      <form-button
                        v-for="(action, actionIndex) of computedActions?.custom"
                        v-show="shouldShowCustomAction(action, item)"
                        :key="actionIndex"
                        v-tooltip="action.tooltip ? action.tooltip : false"
                        :slim="action.text ? false : true"
                        :style="action.text ? '' : 'aspect-ratio: 1'"
                        :icon="action.icon ? action.icon : 'shape-square'"
                        :theme="action.theme ? action.theme : 'lead'"
                        :show-slot="!!action.text"
                        :text="action.text ? action.text : false"
                        @click="
                          runCustomActionHandler(index, item, action)
                        "></form-button>
                      <form-button
                        v-if="computedActions?.baseActions.show"
                        slim="true"
                        style="aspect-ratio: 1"
                        icon="eye"
                        :show-slot="false"></form-button>
                      <form-button
                        v-if="computedActions?.baseActions.edit"
                        slim="true"
                        style="aspect-ratio: 1"
                        icon="pencil"
                        :show-slot="false"
                        @click="goToEdit(item)"></form-button>
                      <form-button
                        v-if="computedActions?.baseActions.delete"
                        slim="true"
                        icon="trash"
                        style="aspect-ratio: 1"
                        theme="danger"
                        :show-slot="false"
                        @click="initDeleteFlow(item)"></form-button>
                      <form-button
                        v-if="shouldShowVersioningUiForItem(item)"
                        slim="true"
                        theme="inverse"
                        style="aspect-ratio: 1"
                        v-tooltip="'core.crudV1.actions.showVersioningUi'"
                        icon="clipboard"
                        :show-slot="false"
                        @click="showVersioningUiForItem(item)"></form-button>
                      <slot
                        :name="'cell-actions-extra-after'"
                        v-bind="{ item, index, crudList: this }"></slot>
                    </div>
                  </div>
                </slot>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Skeleton
        class="margin-xl-top"
        style="min-height: 600px"
        :template="'lines'"
        :count="10"
        :fit-height="false"
        :default-height="500"
        :content-ready="!isLoading">
      </Skeleton>

      <pagination
        v-if="pagination.show"
        v-model:pageSize="pagination.limit"
        v-model:currentPage="pagination.start"
        class="margin-xl-top"
        :total-count="listData.totalCount ? listData.totalCount : 1"
        :handles-page-size="true"
        :page-size="pagination.limit"
        :force-show="true"></pagination>

      <drawer
        v-if="computedIsActionListAsPopup"
        v-model="actionPopupState.show"
        location="inline-end"
        title="core.crudV1.actions.drawerTitle">
        <template #header>
          <div class="text-center margin-xl-bottom">
            <h3 class="margin-remove">
              {{ tt("core.crudV1.actions.drawerTitle") }}
            </h3>
            <p>{{ tu("core.crudV1.actions.drawerExplain") }}</p>
          </div>
        </template>
        <template #default>
          <div class="container container-xs margin-auto-inline">
            <div class="flex-column gap-m max-width-200 margin-auto-inline">
              <slot
                :name="'cell-actions-extra-before'"
                v-bind="{
                  item: actionPopupState.item,
                  index: actionPopupState.index,
                  crudList: this,
                  hideItemActionsPopup,
                }"></slot>

              <form-button
                v-for="(action, actionIndex) of computedActions?.custom"
                v-show="shouldShowCustomAction(action, actionPopupState.item)"
                :key="actionIndex"
                v-tooltip="action.tooltip ? action.tooltip : false"
                :slim="action.text ? false : true"
                :style="action.text ? '' : 'aspect-ratio: 1'"
                :icon="action.icon ? action.icon : 'shape-square'"
                :theme="action.theme ? action.theme : 'lead'"
                :show-slot="!!action.text"
                :text="action.text ? action.text : false"
                @click="
                  runCustomActionHandler(
                    actionPopupState.index,
                    actionPopupState.item,
                    action
                  )
                "></form-button>
              <form-button
                v-if="computedActions?.baseActions?.show"
                icon="eye"
                text="core.crudV1.actions.show"></form-button>
              <form-button
                v-if="computedActions?.baseActions?.edit"
                text="core.crudV1.actions.edit"
                icon="pencil"
                @click="goToEdit(actionPopupState.item)"></form-button>
              <form-button
                v-if="shouldShowVersioningUiForItem(actionPopupState.item)"
                text="core.crudV1.actions.showVersioningUi"
                theme="inverse"
                icon="clipboard"
                @click="
                  showVersioningUiForItem(actionPopupState.item)
                "></form-button>
              <form-button
                v-if="computedActions?.baseActions?.delete"
                icon="trash"
                text="core.crudV1.actions.delete"
                theme="danger"
                @click="initDeleteFlow(actionPopupState.item)"></form-button>

              <slot
                :name="'cell-actions-extra-after'"
                v-bind="{
                  item: actionPopupState.item,
                  index: actionPopupState.index,
                  crudList: this,
                }"></slot>
              <form-button
                icon="x"
                theme="inverse"
                class="margin-2xl-top"
                @click="hideItemActionsPopup"
                text="core.crudV1.actions.closeActions"></form-button>
            </div>
          </div>
        </template>
      </drawer>

      <modal
        size="xl"
        :show="versioningItem"
        @modal:closed="versioningItem = null"
        title="core.crudV1.versioning.mainModalTitle"
        :show-footer="false">
        <div>
          <p class="margin-xl-bottom">
            {{ tu("core.crudV1.versioning.mainModalExplain") }}
          </p>
          <version-manager
            @versionManager:versionAccepted="refreshAsyncData"
            @versionManager:versionDeleted="refreshAsyncData"
            :entity-type="subject"
            :id="versioningItem?.id"></version-manager>
        </div>
      </modal>
    </div>

    <modal
      :show="isShowingConfigEntries"
      :show-footer="false"
      size="2xl"
      title="core.crudV1.configEntries.title"
      @modal:closed="isShowingConfigEntries = false">
      <div>
        <h3>todo: config in v1list</h3>
      </div>
    </modal>

    <modal
      :show="isShowingExportModal"
      size="s"
      title="core.crudV1.actions.exportModalTitle"
      @modal:closed="isShowingConfigEntries = false">
      <template #default>
        <div class="container container-xs">
          <div class="export-ui" v-show="!isExporting">
            <p class="">
              {{ tu("core.crudV1.actions.exportModalExplain") }}
            </p>
            <div class="width-expand margin-m-top">
              <form-input
                :as-user="asUser"
                type="onOffToggle"
                offLabel="core.crudV1.actions.exportCountCurrentLabel"
                onLabel="core.crudV1.actions.exportCountAllLabel"
                label="core.crudV1.actions.exportCountSelectorLabel"
                v-model="exportQueryData.exportAll"></form-input>
            </div>
          </div>
          <div
            class="spinner-container flex flex-middle flex-center width-expand padding"
            v-show="isExporting">
            <spinner :text="exportUiText" size="3"></spinner>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="width-expand flex-middle flex-center">
          <form-button
            theme="success"
            icon="arrow-download-alt"
            class="min-width-200"
            text="core.crudV1.actions.exportFinalCta"
            :disabled="isExporting"
            :loading="isExporting"
            @click="exportRecordsByState()"></form-button>
        </div>
      </template>
    </modal>

    <modal
      :show="isShowingImportModal"
      size="xl"
      title="core.crudV1.actions.importModalTitle"
      @modal:closed="isShowingImportModal = false">
      <template #default>
        <div class="container container-xl relative padding-s-bottom">
          <div
            class="relative"
            v-if="importUiErrorText === '' || !importUiErrorText">
            <spinner
              v-if="importUiLoadingText && importUiLoadingText !== ''"
              overlay="absolute"
              :auto-translate="false"
              :text="computedImportUiLoadingText"></spinner>
            <div
              class="select-data animate__fadeIn animate--faster container container-xs"
              v-show="importStep === 0">
              <h3 class="margin-m-bottom">
                {{ tu("core.crudV1.actions.import.step0Title") }}
              </h3>
              <p>{{ tu("core.crudV1.actions.import.step0Explain") }}</p>

              <form-input
                class="margin-m-top"
                type="select2"
                icon="check-list"
                :multiple="true"
                :list="importFieldList"
                label="core.crudV1.actions.import.selectFieldsLabel"
                v-model="importFields"></form-input>
              <form-input
                class="margin-m-top"
                type="file"
                icon="cloud-upload"
                accept="text/csv"
                label="core.crudV1.actions.import.selectFileLabel"
                v-model="importFile"></form-input>
              <form-input
                class="margin-m-top"
                v-for="field of importExtraFields"
                :key="`import-extra-field-${field.name}`"
                v-bind="field"
                :model-value="importAdditionalData[field.name]"
                @update:modelValue="
                  (val) => (importAdditionalData[field.name] = val)
                ">
              </form-input>
            </div>
            <div
              class="confirm animate__fadeIn animate--faster"
              v-show="importStep === 1">
              <div class="container container-xs text-center">
                <h3 class="margin-m-bottom">
                  {{ tu("core.crudV1.actions.import.step1Title") }}
                </h3>
                <p>
                  {{
                    tu("core.crudV1.actions.import.step1Explain", {
                      count: importData?.length || 0,
                    })
                  }}
                </p>
              </div>

              <div
                class="table-container overflow-auto margin-xl-top"
                style="max-width: 100%; max-height: 50vh">
                <table
                  class="table table--slim"
                  v-if="importData && importForm">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th
                        v-for="(fieldName, fieldIndex) of importFields"
                        :key="`importField${fieldName}${fieldIndex}`">
                        {{ t(importForm.fields[fieldName].label) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, rowIndex) of importData"
                      :key="`importRow${rowIndex}`">
                      <td>{{ rowIndex + 1 }}</td>
                      <td
                        v-for="(fieldName, fieldIndex) of importFields"
                        :key="`importFieldValue${rowIndex}${fieldName}${fieldIndex}`">
                        <span class="text-overflow-ellipsis max-width-200">{{
                          row[fieldName]
                        }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div
              class="summary animate__fadeIn animate--faster"
              v-show="importStep === 2">
              <div class="container container-xs text-center">
                <h3 class="margin-m-bottom">
                  {{ tu("core.crudV1.actions.import.step2Title") }}
                </h3>
                <p>
                  {{
                    tu("core.crudV1.actions.import.step2Explain", {
                      successfulCount: this.importResult.successful.length,
                      failedCount: this.importResult.failed.length,
                    })
                  }}
                </p>
              </div>

              <div
                class="table-container overflow-auto margin-xl-top"
                style="max-width: 100%; max-height: 50vh">
                <h4 class="c-danger" v-if="importResult?.failed?.length > 0">
                  {{ tu("core.crudV1.actions.import.failedItemsTableTitle") }}
                </h4>
                <table
                  class="table table--slim"
                  v-if="importResult?.failed?.length > 0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th
                        v-for="(fieldName, fieldIndex) of importFields"
                        :key="`importField${fieldName}${fieldIndex}`">
                        {{ t(importForm.fields[fieldName].label) }}
                      </th>
                      <th>
                        {{
                          tu(
                            "core.crudV1.actions.import.failedTableFailedReasonHeader"
                          )
                        }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, rowIndex) of importResult?.failed"
                      :key="`importRowFailedResult${rowIndex}`">
                      <td>{{ rowIndex + 1 }}</td>
                      <td
                        v-for="(fieldName, fieldIndex) of importFields"
                        :key="`importFieldValueFailedResult${rowIndex}${fieldName}${fieldIndex}`">
                        <span class="text-overflow-ellipsis max-width-200">{{
                          row?.item?.[fieldName]
                        }}</span>
                      </td>
                      <td>{{ row.errorText }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div v-else class="margin-auto-inline text-center container-xs">
            <h3 class="c-danger margin-m-bottom">
              {{ tu("core.crudV1.actions.import.errorTitle") }}
            </h3>
            <p class="c-danger">{{ tu(importUiErrorText) }}</p>
          </div>
        </div>
      </template>
      <template #footer>
        <div
          class="width-expand flex-middle flex-center relative"
          v-if="!importUiErrorText && importUiErrorText !== ''">
          <form-button
            theme="success"
            icon-end="chevron-inline-end"
            class="min-width-200"
            text="core.crudV1.actions.import.step0Continue"
            v-if="importStep === 0"
            :disabled="!importFields || importFields.length < 1 || !importFile"
            @click="confirmImportStep0"></form-button>

          <form-button
            theme="success"
            icon-end="chevron-inline-end"
            class="min-width-200"
            text="core.crudV1.actions.import.step0Continue"
            v-if="
              importStep === 1 &&
              (importUiLoadingText === '' || !importUiLoadingText)
            "
            @click="confirmImportStep1"></form-button>

          <form-button
            theme="inverse"
            icon="x"
            text="core.close"
            v-if="importStep === 2"
            @click="resetImportUi(true)"></form-button>
        </div>
        <div class="width-expand flex-middle flex-center relative" v-else>
          <form-button
            theme="inverse"
            icon="x"
            text="core.close"
            @click="resetImportUi(true)"></form-button>
        </div>
      </template>
    </modal>
  </block>
</template>

<script>
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import { computed } from "vue";
import _ from "lodash";
import ConfigListView from "@/client/views/admin/config/List";
import ChatView from "@/client/views/Chat.vue";
import VersionManager from "@/client/components/crud/customV1/VersionManager.vue";
import useDom from "@/client/extensions/composition/useDom";

export default {
  name: "CrudV1List",
  components: {
    VersionManager,
    ConfigListView: ConfigListView,
    ChatView,
  },
  props: {
    subject: {
      type: String,
      default: "generic",
    },
    listSrc: {
      type: String,
      default: false,
    },
    showIdField: {
      type: Boolean,
      default: true,
    },
    sortable: {
      type: Boolean,
      default: false,
    },
    initialOrdering: {
      type: Object,
      default: {
        key: "id",
        direction: "desc",
      },
    },
    enableMassActions: {
      type: Boolean,
      default: true,
    },
    customMassActions: {
      type: [Object, Boolean],
      default: false,
    },
    publishable: {
      type: Boolean,
      default: false,
    },
    fields: {
      type: Object,
      default: {},
    },
    actions: {
      type: Object,
      default: {},
    },
    massActions: {
      type: Object,
      default: {},
    },
    /*
     * false - auto, string - route name, object - router config, function - callback
     */
    createRoute: {
      type: [Boolean, String, Object, Function],
      default: false,
    },
    editRoute: {
      type: [Boolean, String, Object, Function],
      default: false,
    },
    createRoutePrefix: {
      type: [String, Boolean],
      default: "",
    },
    reorderRoute: {
      type: [String, Boolean],
      default: false,
    },
    editRoutePrefix: {
      type: [String, Boolean],
      default: "",
    },
    deleteRoute: {
      type: [Boolean, String, Function],
      default: false,
    },
    deleteRoutePrefix: {
      type: [String, Boolean],
      default: "",
    },
    reOrderRoutePrefix: {
      type: String,
      default: "",
    },
    initialFilters: {
      type: [Object, Boolean],
      default: false,
    },
    constantFilters: {
      type: Object,
      default: {},
    },
    additionalGetListData: {
      type: Object,
      default: {},
    },
    noItemsFoundText: {
      type: String,
      default: "core.crud.noItemsFound",
    },
    paginationConfig: {
      type: Object,
      default: () => ({
        start: 0,
        limit: 25,
        show: true,
      }),
    },
    actionListAsPopup: {
      type: Boolean,
      default: false,
    },
    /**
     * Should we show a button to show the config view from our list
     * if false - no config button
     * if true - just shows all configs on click
     * if object:
     *  - filters : filters for the config list
     */
    configEntries: {
      type: [Boolean, Object],
      default: false,
    },
    disableVersioningUi: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["crud-list:items-updated", "crud-list:data-updated"],
  setup(props) {
    let { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations(props);
    let { initiateFileDownload } = useDom(props);
    return { asyncOps, asyncOpsReady, asyncStatus, initiateFileDownload };
  },
  data: function () {
    return {
      tableRefreshIndicator: false,
      refreshIndicatorTimeout: null,
      refreshIndicatorDuration: 1000,
      uniqueRowKeyPrefix: "listv1-" + utilities.getUniqueNumber(),
      listData: {},
      items: [],
      isShowingExportModal: false,
      exportQueryData: {
        exportAll: false,
      },
      isExporting: false,
      exportUiText: false,
      isShowingImportModal: false,
      isImporting: false,
      importUiLoadingText: false,
      importUiLoadingTextParams: {},
      importUiErrorText: false,
      importUiError: "",
      importData: [],
      importAdditionalData: {},
      importForm: false,
      importResult: {
        successful: [],
        failed: [],
      },
      importStep: 0,
      importFields: [],
      importFile: false,
      localColumnVisibility: [],
      isShowingFieldToggleUi: false,
      pagination: this.paginationConfig,
      ordering: this.initialOrdering,
      persistStateTimeout: null,
      filters:
        this.initialFilters && typeof this.initialFilters === "object"
          ? { ...this.initialFilters }
          : {},
      desiredFilters:
        this.initialFilters && typeof this.initialFilters === "object"
          ? { ...this.initialFilters }
          : {},
      filterTimeout: null,
      filterDelay: 350,
      willFilterSoon: false,
      isLoading: true,
      loadingTimeout: null,
      loadingUiDelay: 200,
      isShowingFilterFields: false,
      isShowingConfigEntries: false,
      canDisplayNoItems: false,
      selectedItems: [],
      actionPopupState: {
        show: false,
        item: {},
        index: 0,
      },
      versioningItem: false,
      // so that we do not persist default state before loading it. this is for perserving list state after refreshes
      stateLoaded: false,

      asyncData: {
        listData: {
          target: computed(() => this.computedListSrc),
          data: computed(() => {
            let additionalData = { ...this.additionalGetListData };
            if (!additionalData.with) {
              additionalData.with = [];
            }

            if (!additionalData.with.includes("versions")) {
              additionalData.with.push("versions");
            }

            return {
              pagination: this.requestPagination,
              filters: this.requestFilters,
              ordering: this.requestOrdering,
              ...additionalData,
            };
          }),
        },
      },
    };
  },
  computed: {
    hasActiveFilters() {
      return Object.values(this.filters).length > 0;
    },
    hasActiveRemoveableActiveFilters() {
      return (
        Object.values(this.filters).length >
        Object.values(this.constantFilters).length
      );
    },
    requestPagination() {
      return this.pagination;
    },
    requestFilters() {
      let result = {};

      for (const [key, value] of Object.entries(this.filters)) {
        if (value === "") {
          continue;
        }

        result[key] = value;
      }
      for (const [key, value] of Object.entries(this.constantFilters)) {
        if (value === "") {
          continue;
        }

        result[key] = value;
      }
      return result;
    },
    requestOrdering() {
      return this.ordering;
    },
    displayItems() {
      return this.listData && this.listData.items ? this.listData.items : [];
    },
    filterFields() {
      if (!this.fields) {
        return {};
      }

      let result = {};
      for (const [key, field] of Object.entries(this.fields)) {
        if (!field.hasOwnProperty("filterable") || !field.filterable) {
          continue;
        }

        result[key] = {
          itemKey: field.itemKey,
          name: field.itemKey,
          type: "text",
          placeholder: field.label,
        };

        if (field.field) {
          // allow override from the field key
          result[key] = { ...result[key], ...field.field };
        }

        // allow override when filterable is object
        if (typeof field.filterable === "object") {
          result[key] = {
            ...result[key],
            ...field.filterable,
          };
        }
      }

      return result;
    },
    hasFilterFields() {
      let filters = this.displayFilterFields;
      return Object.entries(filters).length > 0;
    },
    displayFilterFields() {
      let fields = this.filterFields;
      let result = {};
      for (const [key, field] of Object.entries(fields)) {
        if (field.hardCoded) {
          continue;
        }

        if (field.hidden) {
          continue;
        }

        result[key] = field;
      }

      if (this.isAnyItemVersionable && !this.disableVersioningUi) {
        result["hasVersionSuggestion"] = {
          name: "hasVersionSuggestion",
          label: false,
          placeholder: "core.crudV1.versioning.hasVersionSuggestion",
          type: "select2",
          list: [
            { value: undefined, label: "core.all" },
            { value: true, label: "core.yes" },
            { value: false, label: "core.no" },
          ],
        };
      }
      return result;
    },
    hasHiddenFields() {
      return (
        Object.keys(this.allPossibleDisplayFields).length >
        Object.keys(this.displayFields).length
      );
    },
    allPossibleDisplayFields() {
      if (!this.fields) {
        return {};
      }

      let all = {};
      for (const [key, field] of Object.entries(this.fields)) {
        let safeField = {
          ...field,
        };

        if (field.listable === false) {
          continue;
        }
        // enforce integrity
        if (!safeField.hasOwnProperty("sortable")) {
          safeField.sortable = true;
        }

        if (!safeField.hasOwnProperty("label")) {
          safeField.label = "core.crudV1.fields.noLabel";
        }

        if (!safeField.hasOwnProperty("label")) {
          safeField.label = "core.crudV1.fields.noLabel";
        }

        if (!safeField.hasOwnProperty("orderable")) {
          safeField.orderable = true;
        }

        if (!safeField.hasOwnProperty("quickEdit")) {
          safeField.quickEdit = false;
        }

        all[key] = safeField;
      }

      return all;
    },
    displayFields() {
      let result = {};
      for (const [key, field] of Object.entries(
        this.allPossibleDisplayFields
      )) {
        field.originalKey = key;
        if (
          typeof field.hideableInList === "undefined" ||
          field.hideableInList === false
        ) {
          result[key] = field;
          continue;
        }
        if (!this?.localColumnVisibility) {
          continue;
        }

        if (this?.localColumnVisibility.includes(key)) {
          result[key] = field;
        }
      }

      // sort the fields
      let asArray = Object.values(result);
      asArray.sort((a, b) => {
        let aHideableIndex = this?.localColumnVisibility?.indexOf(
          a.originalKey
        );
        let bHideableIndex = this?.localColumnVisibility?.indexOf(
          b.originalKey
        );
        if (!aHideableIndex) {
          return -1;
        }
        if (!bHideableIndex) {
          return 1;
        }

        return aHideableIndex - bHideableIndex;
      });
      let sortedResult = {};
      asArray.forEach((item) => {
        sortedResult[item.originalKey] = item;
      });

      return sortedResult;
    },
    toggleableFieldList() {
      let result = [];

      for (const [key, field] of Object.entries(
        this.allPossibleDisplayFields
      )) {
        result.push({
          key,
          value: key,
          label: field.label,
        });
      }
      return result;
    },
    computedListSrc() {
      if (this.listSrc) {
        return this.listSrc;
      }

      return this.listSrcPrefix + this.subject;
    },
    computedOrderApiUrl() {
      if (this.reorderRoute) {
        return this.reorderRoute;
      }

      return this.reOrderRoutePrefix + `entity/${this.subject}/setOrdering`;
    },
    areAllItemsSelected() {
      return (
        this.selectedItems.length > 0 &&
        this.selectedItems.length === this.displayItems.length
      );
    },
    areItemsSelected() {
      return this.isAnyItemSelected;
    },
    isAnyItemSelected() {
      return this.selectedItems.length > 0;
    },
    selectedIds() {
      return this.selectedItems.map((item) => item.id);
    },
    hasComputedTopActions() {
      // if there  a custom mass action - it appears on top
      if (Object.entries(this.computedMassActions).length > 0) {
        return true;
      }
      // the delete and create base actions also entail mass actions.

      if (this.computedActions.baseActions?.create) {
        return true;
      }

      if (this.computedActions.baseActions?.delete) {
        return true;
      }

      return false;
    },
    computedActions() {
      if (this.actions === false) {
        return false;
      }
      let result = {
        baseActions: {},
        custom: {},
      };

      if (this.actions.show !== false) {
        result.baseActions.show = true;
      }

      if (this.actions.edit !== false) {
        result.baseActions.edit = true;
      }

      if (this.actions.delete !== false) {
        result.baseActions.delete = true;
      }

      if (this.actions.create !== false) {
        result.baseActions.create = true;
      }

      if (!this.actions.custom || typeof this.actions.custom !== "object") {
        return result;
      }

      for (const [name, conf] of Object.entries(this.actions.custom)) {
        result.custom[name] = conf;
      }

      return result;
    },
    computedMassActionsEnabled() {
      if (!this.enableMassActions) {
        return false;
      }

      if (!this.massActions) {
        return false;
      }

      return true;
    },
    computedMassActions() {
      let result = {
        delete: true,
        export: true,
        import: false,
        custom: {},
      };

      if (!this.computedMassActionsEnabled) {
        return {};
      }

      // base actions - delete
      if (
        this.massActions.hasOwnProperty("delete") &&
        !this.massActions.delete
      ) {
        result.delete = false;
      }

      // base actions - export
      if (this.massActions.hasOwnProperty("export")) {
        if (!this.massActions.export) {
          result.export = false;
        }

        if (
          this.massActions.export &&
          typeof this.massActions.export === "object"
        ) {
          result.export = this.massActions.export;
        }
      }

      // base actions - import
      if (this.massActions.hasOwnProperty("import")) {
        if (!this.massActions.import) {
          result.import = false;
        } else {
          result.import = true;
        }

        if (
          this.massActions.import &&
          typeof this.massActions.import === "object"
        ) {
          result.import = this.massActions.import;
        }
      }

      //  custom actions, if available
      if (
        !this.massActions.custom ||
        typeof this.massActions.custom !== "object"
      ) {
        return result;
      }

      for (const [name, conf] of Object.entries(this.massActions.custom)) {
        result.custom[name] = conf;
      }

      return result;
    },
    hasConstantMassActions() {
      let result = false;
      if (!this.computedMassActions) {
        return false;
      }

      let custom = {};

      if (this.computedMassActions.custom) {
        custom = { ...this.computedMassActions.custom };
      }

      let allActions = { ...this.computedMassActions, ...custom };
      delete allActions.custom;

      Object.values(allActions).forEach((action) => {
        if (
          action &&
          typeof action === "object" &&
          action?.alwaysShow === true
        ) {
          result = true;
          return false;
        }
      });
      return result;
    },
    computedConfigEntries() {
      if (!this.configEntries) {
        return false;
      }

      if (typeof this.configEntries !== "object") {
        return {};
      }

      return this.configEntries;
    },
    isAnyItemVersionable() {
      if (!Array.isArray(this.listData?.items) || !this.listData?.items > 0) {
        return false;
      }

      let hasVersionable = false;
      this.listData?.items.forEach((item) => {
        if (item.versionable) {
          hasVersionable = true;
          return false;
        }
      });
      return hasVersionable;
    },
    showVersionCountColumn() {
      if (!this.isAnyItemVersionable) {
        return false;
      }
      if (!Array.isArray(this.listData?.items) || !this.listData?.items > 0) {
        return false;
      }

      let hasVersionCountColumn = false;
      this.listData?.items.forEach((item) => {
        if (typeof item.versionCount !== "undefined") {
          hasVersionCountColumn = true;
          return false;
        }
      });
      return hasVersionCountColumn;
    },
    showHasUnacceptedVersionSuggestionColumn() {
      if (!this.isAnyItemVersionable) {
        return false;
      }
      if (!Array.isArray(this.listData?.items) || !this.listData?.items > 0) {
        return false;
      }

      let showUnacceptedVersionSuggestion = false;
      this.listData?.items.forEach((item) => {
        if (typeof item.hasUnacceptedVersionSuggestion !== "undefined") {
          showUnacceptedVersionSuggestion = true;
          return false;
        }
      });
      return showUnacceptedVersionSuggestion;
    },
    computedIsActionListAsPopup() {
      if (this.actionListAsPopup) {
        return true;
      }

      let count = 0;
      if (this.computedActions.baseActions.edit) {
        count++;
      }
      if (this.computedActions.baseActions.delete) {
        count++;
      }
      if (this.computedActions.baseActions.view) {
        count++;
      }
      count = count + Object.keys(this.computedActions.custom).length;

      if (this.isAnyItemVersionable) {
        count++;
      }

      return count > 3;
    },
    exportItemParser() {
      if (
        !this.computedMassActions.export ||
        typeof this.computedMassActions.export !== "object"
      ) {
        return this.defaultParseItemForExport;
      }

      if (typeof this.computedMassActions.export?.itemParser === "function") {
        return this.computedMassActions.export?.itemParser;
      }
    },
    importFieldList() {
      if (!this?.importForm?.fields) {
        return [];
      }
      return Object.values(this.importForm.fields)
        .map((item) => {
          return {
            value: item.name,
            label: item.name + ` (${this.t(item.label)})`,
          };
        })
        .filter((item) => {
          let aclResult = this.$acl.can(this.subject, "create", [item.value]);
          return aclResult;
        });
    },
    importExtraFields() {
      let result = {};
      if (!this.computedMassActions.import.additionalFields) {
        return result;
      }

      return this.computedMassActions.import.additionalFields;
    },
    computedImportUiLoadingText() {
      return this.tu(this.importUiLoadingText, this.importUiLoadingTextParams);
    },
  },
  watch: {
    listData: {
      deep: true,
      handler(newVal, oldVal) {
        this.deselectAllItems();
        this.$emit("crud-list:items-updated", newVal.items);
        this.$emit("crud-list:data-updated", newVal);
        if (!newVal || !newVal.items) {
          return;
        }

        this.hideTableRefreshIndicator();
        this.$nextTick(() => {
          this.showTableRefreshIndicator();
        });

        // }
      },
    },
    "asyncOps.asyncStatus.asyncDataLoading": {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadingTimeout = utilities.setClientTimeout(() => {
            this.isLoading = true;
          }, this.loadingUiDelay);
          this.canDisplayNoItems = true;
        } else {
          clearTimeout(this.loadingTimeout);
          this.isLoading = false;
        }
      },
    },
    desiredFilters: {
      deep: true,
      handler(newVal) {
        clearTimeout(this.filterTimeout);
        this.willFilterSoon = true;
        this.filterTimeout = utilities.setClientTimeout(() => {
          let newFilters = {};
          for (const [key, value] of Object.entries(newVal)) {
            if (value === null || value === undefined || value === "") {
              continue;
            }
            newFilters[key] = value;
          }
          this.filters = newFilters;
          this.willFilterSoon = false;
        }, this.filterDelay);

        this.persistState();
      },
    },
    filters: {
      deep: true,
      handler(newVal) {
        this.persistState();
      },
    },
    fields: {
      handler(newVal) {
        if (!newVal || typeof newVal !== "object") {
          return true;
        }

        for (const [key, field] of Object.entries(newVal)) {
          if (
            !this?.localColumnVisibility.includes(key) &&
            field.visibleInListByDefault
          ) {
            this?.localColumnVisibility.push(key);
          }
        }

        this.persistState();
      },
      immediate: true,
      deep: true,
      localColumnVisibility: {
        handler() {
          this.persistState();
        },
        deep: true,
      },
    },
    pagination: {
      handler() {
        this.persistState();
      },
      deep: true,
    },
    displayFields: {
      handler() {
        this.persistState();
      },
      deep: true,
    },
  },
  created() {
    this.loadState();
  },
  methods: {
    getRandomInt() {
      return utilities.getRandomInt();
    },
    async onReorder(e) {
      let list,
        data = {},
        reqData;
      if (!this.computedOrderApiUrl) {
        return false;
      }

      //notice: we are reloading state from server and working really hard, becuase aparently vue has a bug in reordering array + rendering it
      function move(input, from, to) {
        let numberOfDeletedElm = 1;

        const elm = input.splice(from, numberOfDeletedElm)[0];

        numberOfDeletedElm = 0;

        input.splice(to, numberOfDeletedElm, elm);
      }

      list = [...this.listData.items];
      move(list, e.oldIndex, e.newIndex);
      list.forEach((item, index) => {
        data[index] = item.id;
      });
      reqData = { newOrdering: data, entityType: this.entityType };

      this.$store.commit("ui/showGlobalSpinner", "");

      let result = await this.asyncOps.asyncCall(
        this.computedOrderApiUrl,
        reqData,
        {
          method: "patch",
        }
      );
      if (result.isError) {
        this.$store.commit("ui/hideGlobalSpinner");
        this.$saffron.ui.notification(
          this.translate("core.errorOccurredGeneric"),
          "error"
        );
      }

      this.asyncOps.clearAllRequestDataFromCache();
      this.listData.items = [];
      try {
        await this.refreshAsyncData();
      } catch (e) {}

      this.$store.commit("ui/hideGlobalSpinner");
    },
    persistState() {
      if (!this.stateLoaded) {
        return;
      }
      if (utilities.isSSR()) {
        return;
      }
      clearTimeout(this.persistStateTimeout);
      let persistStateActual = () => {
        let checksum = utilities.safeStringifyToHash(this.$props, true);
        let cookieData = {
          localColumnVisibility: this.localColumnVisibility,
          // pagination and filters will not work like this because they are v-model and change the props.
          // we will need to improve on the hashing.
          //  filters: this.filters,
          //  paginationLimit :this.pagination.limit,
        };

        this.$cookie.set(checksum, cookieData);
      };

      setTimeout(() => {
        persistStateActual();
      }, 100);
    },
    loadState() {
      let safeProps = _.cloneDeep(this.$props);
      delete safeProps.pagination;
      let checksum = utilities.safeStringifyToHash(safeProps, true);
      let cookieValue = this.$cookie.get(checksum);

      if (!cookieValue) {
        this.stateLoaded = true;
        return;
      }

      this.localColumnVisibility = cookieValue?.localColumnVisibility;
      this.stateLoaded = true;
    },
    resetFilters() {
      this.isShowingFilterFields = false;
      this.desiredFilters = { ...this.constantFilters, ...this.initialFilters };
    },
    reverseOrderingDirection() {
      if (this.ordering.direction === "asc") {
        this.ordering.direction = "desc";
      } else {
        this.ordering.direction = "asc";
      }

      return this;
    },
    quickSetListOrdering(key) {
      if (this.isColCurrentOrdering(key)) {
        return this.reverseOrderingDirection();
      }

      this.ordering = {
        key: key,
        direction: "asc",
      };
    },
    isColCurrentOrdering(col) {
      return this.ordering && this.ordering.key && this.ordering.key === col;
    },
    isColCurrentOrderingAsc(col) {
      return (
        this.isColCurrentOrdering(col) &&
        this.ordering &&
        this.ordering.direction &&
        this.ordering.direction === "asc"
      );
    },
    isColCurrentOrderingDesc(col) {
      return (
        this.isColCurrentOrdering(col) &&
        this.ordering &&
        this.ordering.direction &&
        this.ordering.direction === "desc"
      );
    },
    goToCreate() {
      if (typeof this.createRoute === "function") {
        return this.createRoute();
      }

      if (!this.createRoute) {
        return this.$router.push({
          name: this.createRoutePrefix + this.subject + "-create",
        });
      }

      if (typeof this.createRoute === "object") {
        this.$router.push(this.createRoute);
      }

      if (typeof this.createRoute === "string") {
        this.$router.push({ name: this.createRoute });
      }
    },
    goToEdit(item) {
      if (typeof this.editRoute === "function") {
        return this.editRoute(item);
      }

      if (!this.editRoute) {
        return this.$router.push({
          name: this.editRoutePrefix + this.subject + "-edit-id",
          params: { id: item.id },
        });
      }

      if (typeof this.editRoute === "object") {
        let pushTarget = { ...this.editRoute };
        if (!pushTarget.params) {
          pushTarget.params = {};
        }

        if (!pushTarget.params.id) {
          pushTarget.params.id = item.id;
        }
        this.$router.push(pushTarget);
      }

      if (typeof this.editRoute === "string") {
        this.$router.push({
          name: this.editRoute,
          params: { id: item.id },
        });
      }
    },
    getDeleteRoute(items) {
      if (typeof this.deleteRoute === "function") {
        return this.deleteRoute(items);
      }

      if (typeof this.deleteRoute === "string" && this.deleteRoute !== "") {
        return this.deleteRoute;
      }

      if (typeof this.deleteRoute === "string" && this.deleteRoute !== "") {
        return this.deleteRoute;
      }

      return this.deleteRoutePrefix + this.subject;
    },
    async deleteItem(item) {
      return this.deleteItems([item]);
    },
    async deleteSelectedItems() {
      return this.deleteItems(this.selectedItems);
    },
    async deleteItems(items) {
      let ids = items.map((item) => {
        if (typeof item === "number") {
          return item;
        }
        if (typeof item !== "object" || !item || !item.hasOwnProperty("id")) {
          debug(
            "invalid argument for delete itesm. each item must either be an ID of an item, or an object with an id property",
            { items, item }
          );
          return 0;
        }
        return item.id;
      });

      let result = await this.asyncOps.asyncCall(
        this.getDeleteRoute(),
        { ids },
        { method: "delete" }
      );

      if (result.isError) {
        return this.$s.ui.notification(
          "core.crudV1.deleteActionErrorNotification",
          "error"
        );
      }

      this.$s.ui.notification(
        "core.crudV1.deleteActionSuccessNotification",
        "success"
      );
      this.refreshAsyncData();
    },
    initDeleteFlow(item) {
      this.$s.ui.modal.confirm("core.crudV1.deleteModalConfirmText").then(
        () => {
          this.deleteItems([item]);
        },
        () => {
          this.$s.ui.notification(
            "core.crudV1.deleteActionCanceledNotification"
          );
        }
      );
    },
    initDeleteSelectedItemsFlow() {
      this.$s.ui.modal.confirm("core.crudV1.deleteModalConfirmText").then(
        () => {
          this.deleteSelectedItems();
        },
        () => {
          this.$s.ui.notification(
            "core.crudV1.deleteActionCanceledNotification"
          );
        }
      );
    },
    selectItem(item) {
      if (!this.selectedItems.includes(item)) {
        this.selectedItems.push(item);
      }

      return this;
    },
    deselectItem(item) {
      let index = this.selectedItems.indexOf(item);
      if (index < 0) {
        return;
      }
      this.selectedItems.splice(index, 1); // 2nd parameter means remove one item only
    },
    toggleItemSelection(item) {
      if (this.isItemSelected(item)) {
        this.deselectItem(item);
      } else {
        this.selectItem(item);
      }
    },
    selectAllItems() {
      let itemsCopy = [].concat(this.displayItems);
      for (const item of itemsCopy) {
        if (!this.isItemSelected(item)) {
          this.selectItem(item);
        }
      }
    },
    deselectAllItems() {
      this.selectedItems.length = 0;
    },
    isItemSelected(target) {
      let targetId = typeof target === "object" ? target.id : target;
      let result = this.selectedItems.find((item) => {
        return item.id == targetId;
      });

      return typeof result !== "undefined";
    },
    toggleAllSelection() {
      if (this.areAllItemsSelected) {
        this.deselectAllItems();
      } else {
        this.selectAllItems();
      }
    },
    async runCustomActionHandler(key, item, actionConfig) {
      if (actionConfig.handler && typeof actionConfig.handler === "function") {
        try {
          await actionConfig.handler({
            crudList: this,
            item: item,
            actionConfig,
          });
          this.hideItemActionsPopup();
        } catch (e) {}
      }
    },
    async runCustomMassActionHandler(key, actionConfig) {
      if (actionConfig.handler && typeof actionConfig.handler === "function") {
        actionConfig.handler({
          crudList: this,
          items: this.selectedItems,
          actionConfig,
        });
      }
    },
    async reloadItems() {
      return this.refreshAsyncData();
    },
    hideTableRefreshIndicator() {
      clearTimeout(this.refreshIndicatorTimeout);
      this.tableRefreshIndicator = false;
    },
    showTableRefreshIndicator() {
      this.hideTableRefreshIndicator();
      this.$nextTick(() => {
        this.tableRefreshIndicator = true;

        if (utilities.isClient()) {
          this.refreshIndicatorTimeout = setTimeout(() => {
            this.hideTableRefreshIndicator();
          }, this.refreshIndicatorDuration);
        } else {
          this.hideTableRefreshIndicator();
        }
      });
    },
    setFilters(filters) {
      this.desiredFilters = { ...this.desiredFilters, ...filters };
    },
    async startQuickEdit(item, field) {
      let conf = field.quickEdit;
      if (!conf || typeof conf !== "object") {
        conf = {};
      }
      let type = conf.type ? conf.type : "prompt";
      let url = conf.url ? conf.url : `${this.computedListSrc}/${item.id}`;
      let targetKey = conf.targetKey ? conf.targetKey : field.itemKey;
      let label = conf.label ? conf.label : field.label;
      let promptResult;
      let editResult;
      let newValue;
      try {
        switch (type) {
          case "binary":
            newValue = !item[targetKey];
            break;
          case "binaryInteger":
            newValue = !item[targetKey];
            if (newValue) {
              newValue = 1;
            } else {
              newValue = 0;
            }
            break;
          case "prompt":
            const editField =
              conf.field && typeof conf.field === "object" ? conf.field : {};
            promptResult = await this.$s.ui.modal.prompt(
              "core.crud.quickEditPromptExplain",
              "core.crud.quickEditPromptTitle",
              {
                field: { label: label, name: targetKey, ...editField },
                initialValue: item[field.itemKey],
              }
            );
            newValue =
              promptResult && typeof promptResult === "object"
                ? promptResult.data[targetKey]
                : promptResult;
            break;
        }

        editResult = await this.asyncOps.asyncCall(
          url,
          { [targetKey]: newValue },
          { method: "patch" }
        );
      } catch (e) {
        this.$s.ui.notification("core.crud.quickEditAborted");
        return;
      }

      // parse the response, to extract value to update. this is future proofed (for multiple fields/complext data)
      if (editResult.isError) {
        this.$s.ui.notification("core.crud.quickEditError", "error");
        return;
      }
      // success
      item[field.itemKey] = newValue;

      this.$s.ui.notification("core.crud.quickEditSuccess", "success");
    },
    shouldShowCustomAction(action, item) {
      if (typeof action.show === "undefined") {
        return true;
      }

      if (action.show === false) {
        return false;
      }

      if (typeof action.show === "function") {
        return action.show(item);
      }

      return true;
    },
    showItemActionsPopup({ item, index }) {
      this.actionPopupState.item = item;
      this.actionPopupState.index = index;
      this.actionPopupState.show = true;
    },
    hideItemActionsPopup() {
      this.actionPopupState.show = false;
    },
    shouldShowVersioningUiForItem(item) {
      if (this.disableVersioningUi) {
        return false;
      }

      return !!item.versionable;
    },
    showVersioningUiForItem(item) {
      this.versioningItem = item;
      this.actionPopupState.show = false;
    },
    initExportFlow() {
      this.resetExportUi();
      this.isShowingExportModal = true;
    },
    initImportFlow() {
      this.resetImportUi();
      this.isShowingImportModal = true;
    },
    async resetExportUi() {
      this.exportQueryData.exportAll = false;
      this.isShowingExportModal = false;
      this.isExporting = false;
      this.exportUiText = false;
    },
    async resetImportUi(close = false) {
      this.importResult = {
        successful: [],
        failed: [],
      };
      this.importData = [];
      this.isImporting = false;
      this.importUiLoadingText = false;
      this.importUiErrorText = false;
      this.importAdditionalData = this.computedMassActions?.import
        ?.defaultAdditionalData
        ? { ...this.computedMassActions?.import?.defaultAdditionalData }
        : {};
      this.importStep = 0;
      this.importFields = this.computedMassActions?.import?.defaultFields || [];
      this.importFile = false;

      if (close) {
        this.isShowingImportModal = false;
      } else {
        this.loadImportForm();
      }
    },
    goToImportStep(step) {
      this.importStep = step;
    },
    async loadImportForm() {
      this.importUiLoadingText =
        "core.crudV1.actions.import.loadingDataStructure";

      let url =
        this.computedMassActions?.import?.formUrl ||
        `entity/${this.subject}/create`;
      let result = await this.asyncOps.get(url);
      this.importUiLoadingText = false;

      if (result.hasError) {
        this.importUiErrorText =
          "core.crudV1.actions.import.errorGettingDataStructure";
        return;
      }
      this.importForm = result.data;

      // generate url
      // get the form
      // save to state
    },
    async exportRecordsByState() {
      this.isExporting = true;
      this.exportUiText = "core.crudV1.actions.exportUiTextLoadingData";

      let importData = {
        ...this.additionalGetListData,
        pagination: { ...this.asyncData.listData.data.pagination },
        filters: { ...this.asyncData.listData.data.filters },
        ordering: { ...this.asyncData.listData.data.ordering },
      };

      if (this.exportQueryData.exportAll) {
        importData.pagination.limit = 50000;
      }

      let requestResult = await this.asyncOps.asyncCall(
        this.asyncData.listData.target,
        importData
      );

      if (requestResult.hasError) {
        this.resetExportUi();
        this.$s.ui.notification(
          "core.crudV1.actions.exportErrorFetchError",
          "error"
        );
        return;
      }

      this.exportUiText = "core.crudV1.actions.exportUiTextParsingData";
      let items = requestResult.data.items;
      let data = items.map((item) => this.exportItemParser(item));

      this.exportUiText = "core.crudV1.actions.exportUiTextPreparingDownload";
      const papa = await require("papaparse");
      const csv = "\uFEFF" + papa.unparse(data);
      this.initiateFileDownload(csv, "text/csv", "export");
      this.resetExportUi();
      return true;
    },
    defaultParseItemForExport(
      item,
      parentKey = "",
      result = {},
      parseItem = this.defaultParseItemForExport
    ) {
      for (const [key, value] of Object.entries(item)) {
        const fullKey = parentKey ? `${parentKey}_${key}` : key;
        let regexSafeKey = key.length > 2000 ? key.slice(0, 2000) : key;

        if (_.isPlainObject(value) && /^[A-Z]/.test(regexSafeKey)) {
          parseItem(value, fullKey, result);
        } else {
          result[fullKey] = _.isArray(value) ? JSON.stringify(value) : value;
        }
      }
      return result;
    },

    async parseImportCsvFile(file) {
      let data, meta;
      function papaParseAsync(file, config = {}) {
        return new Promise( (resolve, reject) => {
          const Papa =  require("papaparse");
          Papa.parse(file, {
            ...config,
            complete: resolve,
            error: reject,
          });
        });
      }

      try {
        ({ data, meta } = await papaParseAsync(file, {
          header: true,
          skipEmptyLines: "greedy",
          dynamicTyping: false,
        }));
      } catch (err) {
        return false;
      }

      /* ---------- 2. Header check ---------- */
      return {
        fields: meta.fields,
        rows: data,
      };
    },

    async confirmImportStep0() {
      this.goToImportStep(1);

      if (!this.importFile?.length) {
        this.importUiErrorText = "core.crudV1.actions.import.errorNoFile";
        return;
      }

      const file = this.importFile[0];
      let data = await this.parseImportCsvFile(file);
      if (!data) {
        this.importUiErrorText = "core.crudV1.actions.import.errorParseError";
        return;
      }
      const headers = data.fields;
      const expected = this.importFields;
      const hasHeaderMismatch =
        headers.length !== expected.length ||
        headers.some((h, i) => h !== expected[i]);
      // verify columns
      if (hasHeaderMismatch) {
        this.importUiErrorText =
          "core.crudV1.actions.import.errorHeaderMismatch";
        return;
      }

      let parser = this?.computedMassActions?.import?.itemParser;
      if (typeof parser === "function") {
        data.rows = data.rows.map((item) => parser(item));
      }
      this.importData = data.rows;

      // get the first file.
      // parse it
      // verify, error if:
      // - no records
      // - header mismatch
      // set the data to the state
    },

    async confirmImportStep1() {
      let url = `entity/${this.subject}/mass-create`;
      this.importResult.successful = [];
      this.importResult.failed = [];

      let batchSize = this.computedMassActions?.import?.batchSize || 50;
      let startIndex = 0;

      this.importUiLoadingText = "core.crudV1.actions.import.uploadInProgress";
      let doBatch = async (data) => {
        let callData = {
          ...this.importAdditionalData,
          entityType: this.subject,
          entityData: data,
        };

        let uploadResult = await this.asyncOps.post(url, callData, {
          timeout: 5 * 60 * 1000, // 5 minutes timeout for this heavy request
        });

        if (uploadResult.hasError) {
          data.forEach((item) => {
            this.importResult.failed.push({
              item,
              errorText:
                "request failed" +
                (typeof uploadResult.data === "string"
                  ? " (" + uploadResult.data + ")"
                  : ""),
            });
          });
          return false;
        }

        this.importResult.successful = [
          ...this.importResult.successful,
          ...uploadResult.data.successfulItems,
        ];
        this.importResult.failed = [
          ...this.importResult.failed,
          ...uploadResult.data.failedItems,
        ];
      };



      let batchCount = Math.ceil(this.importData.length / batchSize);
      for (let i = 0; i < batchCount; i++) {
        let items = this.importData.slice(startIndex, startIndex + batchSize);
        this.importUiLoadingText =
          "core.crudV1.actions.import.uploadInProgressBatch";
        let lastRecord = (i + 1) * batchSize + 1;
        if (lastRecord > this.importData.length) {
          lastRecord = this.importData.length;
        }

        this.importUiLoadingTextParams = {
          current: i + 1,
          total: batchCount,
          batchSize: items.length,
          startRecord: i * batchSize + 1,
          endRecord: lastRecord,
        };
        await doBatch(items);
        // in case the server is working hard - give it a sec to do other stuff
        if (i < batchCount - 1) {
          await utilities.wait(1000);
        }

        startIndex = startIndex + batchSize;
      }

      this.importUiLoadingText = false;

      this.goToImportStep(2);
      this.reloadItems();
    },
  },
  serverPrefetch() {
    return this.getSSRPrefetchPromise();
  },
  mounted() {},
};
</script>

<style scoped lang="scss">
.crud-list-table {
  &.is-refreshed {
    position: relative;

    &::after {
      border-radius: var(--border-radius-s);
      content: " ";
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      pointer-events: none;
      z-index: 5;
      background-color: rgba(0, 0, 0, 0);
      animation: pulse-list-table 1s infinite;
    }
  }

  th.orderable {
    cursor: pointer;
    transition: opacity 200ms ease, color 200ms ease;
    color: var(--c-inverse);

    &:hover {
      opacity: 0.8;
      color: var(--c-alt);
    }
  }

  th.is-current-ordering {
    color: var(--c-alt);
  }

  th:not(.orderable) {
    cursor: not-allowed;
    color: var(--c-inverse);
  }

  th.item-select-th {
    cursor: auto;
  }

  @-webkit-keyframes pulse-list-table {
    0% {
      background-color: rgba(0, 0, 0, 0);
    }
    30% {
      background-color: rgba(0, 0, 0, 0.1);
    }
    100% {
      background-color: rgba(0, 0, 0, 0);
    }
  }
  @keyframes pulse-list-table {
    0% {
      background-color: rgba(0, 0, 0, 0);
    }
    30% {
      background-color: rgba(0, 0, 0, 0.1);
    }
    100% {
      background-color: rgba(0, 0, 0, 0);
    }
  }

  .has-quick-edit {
    cursor: pointer;
  }
}
</style>
